import { useMemo } from "react";
import cx from "classnames";

import LinkWithLocale, { LinkWithLocaleProps } from "components/LinkWithLocale";

import Loading from "resources/icons/loading.svg";

import styles from "./Button.module.scss";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "quaternary"
  | "chip"
  | "subtle"
  | "warning"
  | "error";
export type ButtonSize = "small" | "medium" | "large";

// Common shared props
interface CommonProps {
  children?: React.ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  state?: "default" | "active" | "loading" | "disabled" | "skeleton";
  icon?: React.ReactNode;
  className?: string;
}

// Anchor-specific props (only valid when `href` is present)
type AnchorOnlyProps = {
  href: LinkWithLocaleProps["href"];
} & Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  "href" | "type" | "disabled"
>;

// Button-specific props (no `href`, so can't have anchor-only attributes like `target`, `rel`)
type ButtonOnlyProps = {
  href?: undefined;
} & Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "href" | "target" | "rel"
>;

export type Props = CommonProps & (AnchorOnlyProps | ButtonOnlyProps);

const Button = ({
  children,
  variant = "primary",
  size = "large",
  state = "default",
  icon,
  className = "",
  ...rest
}: Props) => {
  const isAnchor = "href" in rest && rest.href !== undefined;

  const isDisabled =
    state === "disabled" || state === "loading" || state === "skeleton";

  const renderIcon = useMemo(() => {
    if (state === "loading") {
      return <Loading className={cx(styles.icon, styles.iconLoading)} />;
    }

    if (icon) {
      return <span className={styles.icon}>{icon}</span>;
    }

    return null;
  }, [icon, state]);

  const buttonClass = cx(
    styles.button,
    styles[variant],
    styles[size],
    styles[state],
    {
      [styles.iconOnly]: icon && !children,
    },
    className
  );

  if (isAnchor) {
    const { href, onClick, ...anchorProps } = rest as AnchorOnlyProps;
    return (
      <LinkWithLocale href={href}>
        <a className={buttonClass} onClick={onClick} {...anchorProps}>
          {renderIcon}
          {children && <span className={styles.text}>{children}</span>}
        </a>
      </LinkWithLocale>
    );
  }

  const { type, onClick, ...buttonProps } = rest as ButtonOnlyProps;

  return (
    <button
      type={type}
      className={buttonClass}
      disabled={isDisabled}
      onClick={onClick}
      {...buttonProps}
    >
      {renderIcon}
      {children && <span className={styles.text}>{children}</span>}
    </button>
  );
};

export default Button;
